import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Package } from '../../types/Package';
import { Gallery } from '../../types/Gallery';
import { BlogPost } from '../../types/Blog';
import { getOptimizedImageUrl } from '../../utils/CloudinaryUtils';
import ShareButton from '../Sharing/ShareButton';
import { generateShareContent } from '../../utils/sharingUrls';
import { handleWhatsAppContact } from '../../utils/whatsappContact';
import { formatCurrency, formatAddonPrice, convertCurrencyWithMarkup, loadCurrencySettings } from '../../utils/CurrencyUtils';
import { useCurrency } from '../../contexts/CurrencyContext';
import { loadLiveGalleries } from '../../utils/GalleryAPI';
import sitePaths from '../../utils/sitePaths';
import { getBlogPostsFromServer } from '../../utils/BlogAPI';
import { loadCategoriesFromServer, Category } from '../../utils/categories';
import './ServiceModal.css';
import InquiryDatePicker from '../Common/InquiryDatePicker';
import { inquiryDateLine, useInquiryDate } from '../../utils/inquiryDate';

interface ProcessStep {
  id: string;
  number: number;
  title: string;
  description: string;
}

interface ExpectationItem {
  id: string;
  icon: string;
  title: string;
  description: string;
}

interface Testimonial {
  id: string;
  content: string;
  author: string;
  service: string;
}

interface ServiceCustomizationData {
  processSteps: ProcessStep[];
  expectations: ExpectationItem[];
  testimonials: Testimonial[];
  bookingNote: string;
  showProcessSteps?: boolean;
  showExpectations?: boolean;
  showTestimonials?: boolean;
}

interface PackageModalProps {
  service: Package | null;
  isOpen: boolean;
  onClose: () => void;
  allPackages?: Package[];
  currentIndex?: number;
  onNavigate?: (index: number) => void;
}

const PackageModal: React.FC<PackageModalProps> = ({ 
  service, 
  isOpen, 
  onClose, 
  allPackages = [], 
  currentIndex = 0, 
  onNavigate 
}) => {
  const navigate = useNavigate();
  const { detectedCurrency, isDetecting } = useCurrency();
  useInquiryDate(false);
  const [customizationData, setCustomizationData] = useState<ServiceCustomizationData | null>(null);
  const [relatedGalleries, setRelatedGalleries] = useState<Gallery[]>([]);
  const [relatedBlogPosts, setRelatedBlogPosts] = useState<BlogPost[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    email: 'hello@carmenlorraine.com',
    phone: '+27 (0) 82 123 4567'
  });
  const [convertedPrices, setConvertedPrices] = useState<{
    price: number | null;
    originalPrice: number | null;
    discountAmount: number | null;
    addons: Array<{ name: string; price: number | null; description?: string; pricingType?: string; showFromPrice?: boolean }>;
    currency: string;
  }>({
    price: null,
    originalPrice: null,
    discountAmount: null,
    addons: [],
    currency: 'R'
  });

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const loadedCategories = await loadCategoriesFromServer();
        setCategories(loadedCategories);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Load contact info from localStorage
  useEffect(() => {
    const savedContent = localStorage.getItem('website_content');
    if (savedContent) {
      try {
        const parsed = JSON.parse(savedContent);
        if (parsed.contactEmail) setContactInfo(prev => ({ ...prev, email: parsed.contactEmail }));
        if (parsed.contactPhone) setContactInfo(prev => ({ ...prev, phone: parsed.contactPhone }));
      } catch (error) {
        console.error('Error loading contact info:', error);
      }
    }
  }, []);

  // Load customization data (non-blocking, deferred)
  useEffect(() => {
    if (!service || !isOpen) return;

    // Set default immediately
    setCustomizationData({
      processSteps: [],
      expectations: [],
      testimonials: [],
      bookingNote: 'Free consultation included • Flexible scheduling • Professional results guaranteed',
      showProcessSteps: true,
      showExpectations: true,
      showTestimonials: true
    });

    // Load actual data asynchronously
    const loadData = () => {
      try {
        // Load global customization data
        const globalData = localStorage.getItem('package_customization') || localStorage.getItem('service_customization');
        const globalCustomization = globalData ? JSON.parse(globalData) : {};
        
        if (service?.customization) {
          // Get service-specific process steps
          let processSteps: ProcessStep[] = [];
          if (service.customization.customProcessSteps && service.customization.customProcessSteps.length > 0) {
            // Use custom process steps defined for this service
            processSteps = service.customization.customProcessSteps;
          } else if (service.customization.selectedProcessStepIds && service.customization.selectedProcessStepIds.length > 0) {
            // Use selected global process steps
            const allProcessSteps = globalCustomization.processSteps || [];
            processSteps = allProcessSteps.filter((step: ProcessStep) => 
              service.customization!.selectedProcessStepIds!.includes(step.id)
            );
          } else {
            // Fallback to all global process steps
            processSteps = globalCustomization.processSteps || [];
          }
          
          // Renumber process steps sequentially (1, 2, 3, ...) regardless of original numbers
          processSteps = processSteps
            .sort((a, b) => a.number - b.number) // Sort by original number first
            .map((step, index) => ({
              ...step,
              number: index + 1 // Renumber starting from 1
            }));
          
          // Get service-specific expectations
          let expectations: ExpectationItem[] = [];
          if (service.customization.customExpectations && service.customization.customExpectations.length > 0) {
            // Use custom expectations defined for this service
            expectations = service.customization.customExpectations;
          } else if (service.customization.selectedExpectationIds && service.customization.selectedExpectationIds.length > 0) {
            // Use selected global expectations
            const allExpectations = globalCustomization.expectations || [];
            expectations = allExpectations.filter((item: ExpectationItem) => 
              service.customization!.selectedExpectationIds!.includes(item.id)
            );
          } else {
            // Fallback to all global expectations
            expectations = globalCustomization.expectations || [];
          }
          
          // Get service-specific testimonials
          let testimonials: Testimonial[] = [];
          if (service.customization.customTestimonials && service.customization.customTestimonials.length > 0) {
            // Use custom testimonials defined for this service
            testimonials = service.customization.customTestimonials;
          } else if (service.customization.selectedTestimonialIds && service.customization.selectedTestimonialIds.length > 0) {
            // Use selected global testimonials
            const allTestimonials = globalCustomization.testimonials || [];
            testimonials = allTestimonials.filter((testimonial: Testimonial) => 
              service.customization!.selectedTestimonialIds!.includes(testimonial.id)
            );
          } else {
            // Fallback to all global testimonials
            testimonials = globalCustomization.testimonials || [];
          }
          
          setCustomizationData({
            processSteps: processSteps, // Already sorted and renumbered
            expectations: expectations,
            testimonials: testimonials,
            bookingNote: service.customization.customBookingNote || globalCustomization.bookingNote || 'Free consultation included • Flexible scheduling • Professional results guaranteed',
            showProcessSteps: service.customization.showProcessSteps !== false,
            showExpectations: service.customization.showExpectations !== false,
            showTestimonials: service.customization.showTestimonials !== false
          });
        } else {
          // No service-specific customization, use global defaults
          const savedData = localStorage.getItem('package_customization') || localStorage.getItem('service_customization');
          if (savedData) {
            const globalData = JSON.parse(savedData);
            // Renumber process steps sequentially
            const processSteps = (globalData.processSteps || [])
              .sort((a: ProcessStep, b: ProcessStep) => a.number - b.number)
              .map((step: ProcessStep, index: number) => ({
                ...step,
                number: index + 1
              }));
            
            setCustomizationData({
              processSteps: processSteps,
              expectations: globalData.expectations || [],
              testimonials: globalData.testimonials || [],
              bookingNote: globalData.bookingNote || 'Free consultation included • Flexible scheduling • Professional results guaranteed',
              showProcessSteps: true,
              showExpectations: true,
              showTestimonials: true
            });
          }
        }
      } catch (error) {
        console.error('Error loading customization:', error);
      }
    };

    // Defer loading to next tick
    setTimeout(loadData, 0);
  }, [service, isOpen]);

  // Convert prices when service or currency changes
  useEffect(() => {
    const convertPrices = async () => {
      if (!service || isDetecting) {
        // Use original prices while detecting or if no service
        if (!service) {
          setConvertedPrices({
            price: null,
            originalPrice: null,
            discountAmount: null,
            addons: [],
            currency: 'R'
          });
          return;
        }

        const basePrice = service.isSpecial && service.specialPrice ? service.specialPrice : service.price;
        const baseOriginalPrice = service.price;
        let baseDiscountAmount = 0;
        if (service.isSpecial && 
          service.specialPrice !== undefined && 
          service.price !== undefined && 
          service.specialPrice < service.price) {
          baseDiscountAmount = service.price - service.specialPrice;
        }

        setConvertedPrices({
          price: basePrice || null,
          originalPrice: baseOriginalPrice || null,
          discountAmount: baseDiscountAmount || null,
          addons: service.addons?.map(addon => ({
            name: addon.name,
            price: addon.price || null,
            description: addon.description,
            pricingType: addon.pricingType,
            showFromPrice: addon.showFromPrice
          })) || [],
          currency: service.currency || 'R'
        });
        return;
      }

      const packageCurrency = service.currency || 'R';
      const basePrice = service.isSpecial && service.specialPrice ? service.specialPrice : service.price;
      const baseOriginalPrice = service.price;
      const baseDiscountAmount = Boolean(
        service.isSpecial && 
        service.specialPrice && 
        service.price && 
        service.specialPrice < service.price
      ) ? (service.price! - service.specialPrice!) : 0;

      // If currencies match, no conversion needed
      if (detectedCurrency === packageCurrency) {
        setConvertedPrices({
          price: basePrice || null,
          originalPrice: baseOriginalPrice || null,
          discountAmount: baseDiscountAmount || null,
          addons: service.addons?.map(addon => ({
            name: addon.name,
            price: addon.price || null,
            description: addon.description,
            pricingType: addon.pricingType,
            showFromPrice: addon.showFromPrice
          })) || [],
          currency: packageCurrency
        });
        return;
      }

      // Convert prices
      try {
        const currencySettings = loadCurrencySettings();
        
        // Convert display price
        let convertedPrice = basePrice;
        if (basePrice) {
          const priceConversion = await convertCurrencyWithMarkup(
            basePrice,
            packageCurrency,
            detectedCurrency,
            currencySettings
          );
          convertedPrice = priceConversion.markupAmount;
        }

        // Convert original price
        let convertedOriginalPrice = baseOriginalPrice;
        if (baseOriginalPrice) {
          const originalConversion = await convertCurrencyWithMarkup(
            baseOriginalPrice,
            packageCurrency,
            detectedCurrency,
            currencySettings
          );
          convertedOriginalPrice = originalConversion.markupAmount;
        }

        // Convert discount amount
        let convertedDiscountAmount = baseDiscountAmount;
        if (baseDiscountAmount > 0) {
          const discountConversion = await convertCurrencyWithMarkup(
            baseDiscountAmount,
            packageCurrency,
            detectedCurrency,
            currencySettings
          );
          convertedDiscountAmount = discountConversion.markupAmount;
        }

        // Convert addons
        const convertedAddons = await Promise.all(
          (service.addons || []).map(async (addon) => {
            if (!addon.price) {
              return { name: addon.name, price: null, description: addon.description, pricingType: addon.pricingType, showFromPrice: addon.showFromPrice };
            }
            try {
              const addonConversion = await convertCurrencyWithMarkup(
                addon.price,
                packageCurrency,
                detectedCurrency,
                currencySettings
              );
              return { name: addon.name, price: addonConversion.markupAmount, description: addon.description, pricingType: addon.pricingType, showFromPrice: addon.showFromPrice };
            } catch (error) {
              console.error(`Error converting addon ${addon.name} price:`, error);
              return { name: addon.name, price: addon.price, description: addon.description, pricingType: addon.pricingType, showFromPrice: addon.showFromPrice };
            }
          })
        );

        setConvertedPrices({
          price: convertedPrice || null,
          originalPrice: convertedOriginalPrice || null,
          discountAmount: convertedDiscountAmount || null,
          addons: convertedAddons,
          currency: detectedCurrency
        });
      } catch (error) {
        console.error('Error converting currency:', error);
        // Fallback to original prices on error
        setConvertedPrices({
          price: basePrice || null,
          originalPrice: baseOriginalPrice || null,
          discountAmount: baseDiscountAmount || null,
          addons: service.addons?.map(addon => ({
            name: addon.name,
            price: addon.price || null,
            description: addon.description,
            pricingType: addon.pricingType,
            showFromPrice: addon.showFromPrice
          })) || [],
          currency: packageCurrency
        });
      }
    };

    convertPrices();
  }, [service, detectedCurrency, isDetecting]);

  // Load related content when modal opens
  useEffect(() => {
    if (!service || !isOpen) return;

    const loadRelatedContent = async () => {
      setLoadingRelated(true);
      try {
        // Load galleries
        if (service.relatedGalleryIds && service.relatedGalleryIds.length > 0) {
          const allGalleries = await loadLiveGalleries();
          const filtered = allGalleries.filter(g => service.relatedGalleryIds?.includes(g.id));
          setRelatedGalleries(filtered);
        }

        // Load blog posts
        if (service.relatedBlogIds && service.relatedBlogIds.length > 0) {
          const allPosts = await getBlogPostsFromServer();
          const filtered = allPosts.filter(p => service.relatedBlogIds?.includes(p.id));
          setRelatedBlogPosts(filtered);
        }
      } catch (error) {
        console.error('Error loading related content:', error);
      } finally {
        setLoadingRelated(false);
      }
    };

    loadRelatedContent();
  }, [service, isOpen]);

  const sortedAddons = useMemo(() => {
    const list = convertedPrices.addons || [];
    return [...list].sort((a, b) => {
      const aIncluded = !a.price || a.price === 0;
      const bIncluded = !b.price || b.price === 0;
      if (aIncluded !== bIncluded) return aIncluded ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [convertedPrices.addons]);

  if (!service || !isOpen) return null;

  const price = convertedPrices.price ?? (service.isSpecial && service.specialPrice ? service.specialPrice : service.price);
  const originalPrice = convertedPrices.originalPrice ?? service.price;
  const hasDiscount = Boolean(service.isSpecial && service.specialPrice && service.price && service.specialPrice < service.price);
  const discountAmount = convertedPrices.discountAmount ?? (hasDiscount ? (service.price! - service.specialPrice!) : 0);
  const displayCurrency = convertedPrices.currency;

  // Generate share content with converted prices and currency
  const shareContent = generateShareContent('package', service, {
    displayPrice: price,
    displayCurrency: displayCurrency,
    originalPrice: originalPrice
  });

  const formatDuration = (minutes: number): string => {
    const hours = minutes / 60;
    if (hours === 0.5) return '½ hour';
    if (hours === 0.25) return '¼ hour';
    if (hours === 0.75) return '¾ hour';
    if (hours === 1.5) return '1½ hours';
    if (hours === 2.5) return '2½ hours';
    if (hours === 3.5) return '3½ hours';
    if (hours === 4.5) return '4½ hours';
    if (hours === 5.5) return '5½ hours';
    if (hours === 6.5) return '6½ hours';
    if (hours === 7.5) return '7½ hours';
    if (hours === 8.5) return '8½ hours';
    if (hours % 1 === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${hours} hours`;
  };

  const formatFeatureText = (text: string) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>')
      .replace(/•/g, '<span class="bullet-point">•</span>');
  };

  const formatPricingType = (pricingType?: string) => {
    if (!pricingType) return null;
    if (pricingType === 'per-hour') return 'Per hour';
    if (pricingType === 'per-each') return 'Per item';
    if (pricingType === 'fixed') return 'Fixed';
    return pricingType;
  };

  const getProcessSteps = () => {
    return customizationData?.processSteps || [];
  };

  const getExpectations = () => {
    return customizationData?.expectations || [];
  };

  const getTestimonials = () => {
    return customizationData?.testimonials || [];
  };

  const getBookingNote = () => {
    return customizationData?.bookingNote || 'Free consultation included • Flexible scheduling • Professional results guaranteed';
  };

  const handlePrevious = () => {
    if (allPackages.length > 0 && currentIndex > 0 && onNavigate) {
      onNavigate(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (allPackages.length > 0 && currentIndex < allPackages.length - 1 && onNavigate) {
      onNavigate(currentIndex + 1);
    }
  };

  const canNavigate = allPackages.length > 1;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < allPackages.length - 1;

  // Generate contact messages with package details
  const generateContactMessage = (): string => {
    if (!service) return '';
    
    const parts: string[] = [];
    parts.push('Hi Carmen,');
    parts.push('');
    parts.push(`I'm interested in the *${service.name}* package.`);
    parts.push('');
    
    if (service.short || service.description) {
      parts.push(`Package Details: ${service.short || service.description?.substring(0, 200) || ''}`);
      parts.push('');
    }
    
    if (price) {
      const priceText = formatCurrency(price, displayCurrency);
      parts.push(`Price: ${priceText}`);
      if (originalPrice && originalPrice > price) {
        const originalPriceText = formatCurrency(originalPrice, displayCurrency);
        parts.push(`(was ${originalPriceText})`);
      }
      parts.push('');
    } else {
      parts.push('Please let me know the pricing details.');
      parts.push('');
    }
    
    if (service.durationMinutes) {
      parts.push(`Duration: ${formatDuration(service.durationMinutes)}`);
      parts.push('');
    }
    
    if (service.category) {
      parts.push(`Category: ${service.category}`);
      parts.push('');
    }
    
    parts.push('Could we discuss this package further? I\'d love to learn more about what\'s included and how we can proceed.');
    parts.push('');
    const dateLine = inquiryDateLine();
    if (dateLine) {
      parts.push(dateLine);
      parts.push('');
    }
    parts.push('Looking forward to hearing from you!');
    
    return parts.join('\n');
  };

  // Format phone number for WhatsApp and tel links
  const formatPhoneForUrl = (phone: string): string => {
    return phone.replace(/[\s\(\)\-]/g, '').replace(/^\+/, '');
  };

  // Generate WhatsApp URL
  const getWhatsAppUrl = (): string => {
    const message = encodeURIComponent(generateContactMessage());
    const phone = formatPhoneForUrl(contactInfo.phone);
    return `https://wa.me/${phone}?text=${message}`;
  };

  // Generate Email mailto URL
  const getEmailUrl = (): string => {
    const subject = encodeURIComponent(`Inquiry about ${service?.name || 'Package'}`);
    const body = encodeURIComponent(generateContactMessage());
    return `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
  };

  // Generate Phone tel URL
  const getPhoneUrl = (): string => {
    const phone = formatPhoneForUrl(contactInfo.phone);
    return `tel:${phone}`;
  };

  const handlePrint = () => {
    // Wait for all images in the modal to finish loading before printing
    try {
      const modalEl = document.querySelector('.service-modal');
      if (!modalEl) {
        window.print();
        return;
      }

      const images = Array.from(modalEl.querySelectorAll('img'));
      if (images.length === 0) {
        window.print();
        return;
      }

      const loadPromises = images.map(img => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise<void>((resolve) => {
          const cleanup = () => {
            img.removeEventListener('load', onLoad);
            img.removeEventListener('error', onLoad);
          };
          const onLoad = () => {
            cleanup();
            resolve();
          };
          img.addEventListener('load', onLoad);
          img.addEventListener('error', onLoad);
        });
      });

      Promise.all(loadPromises).then(() => {
        // Small delay to ensure browser has rendered images
        setTimeout(() => window.print(), 50);
      });
    } catch (err) {
      console.error('Error preparing print:', err);
      window.print();
    }
  };

  const modalContent = (
    <div 
      className="service-modal-overlay" 
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="service-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="service-modal-header">
          <div className="header-content">
            <h2 className="service-modal-title">{service.name}</h2>
          </div>
          
          {/* Navigation */}
          {canNavigate && (
            <div className="modal-navigation">
              <button 
                className="nav-button prev" 
                onClick={handlePrevious}
                disabled={!hasPrevious}
                aria-label="Previous package"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <span className="nav-indicator">Package {currentIndex + 1} / {allPackages.length}</span>
              <button 
                className="nav-button next" 
                onClick={handleNext}
                disabled={!hasNext}
                aria-label="Next package"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
          
          <button className="close-button" onClick={onClose} aria-label="Close modal">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Single Scroll Content */}
        <div className="service-modal-content single-scroll">
          {/* Hero Section - Split Layout: Image Left (2/3), Description Right (1/3) */}
          <div className="hero-split-layout">
            {/* Left Side - Hero Image */}
            {service.heroImage && (
              <div className="service-hero-image">
                {service.badge && (
                  <div className={`service-badge-image-overlay ${service.badge.toLowerCase()}`}>
                    <span>{service.badge}</span>
                  </div>
                )}
                <img 
                  src={getOptimizedImageUrl(typeof service.heroImage === 'string' ? service.heroImage : (service.heroImage.secure_url || service.heroImage.publicId || ''), 'hero')}
                  alt={service.name}
                />
                <div className="service-hero-overlay">
                  <div className="hero-overlay-inner">
                    <div className="hero-meta-row">
                      {((service.categories && service.categories.length > 0) || service.category) && (
                        <div className="hero-pills-container">
                          {service.categories && service.categories.length > 0 ? (
                            service.categories.map((categorySlug, idx) => {
                              const category = categories.find(c => c.slug === categorySlug);
                              return (
                                <span 
                                  key={idx} 
                                  className="hero-pill"
                                  style={{ backgroundColor: category?.color || '#8B4513' }}
                                >
                                  <i className="fas fa-tag" />
                                  {category?.name || categorySlug}
                                </span>
                              );
                            })
                          ) : (
                            <span className="hero-pill">
                              <i className="fas fa-tag" />
                              {service.category}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {service.short && (
                      <p className="hero-subtitle">{service.short}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Right Side - About This Package */}
            <div className="hero-description-side">
              {service.description && (
                <div className="hero-description-content">
                  <h3>About This Package</h3>
                  <div
                    className="service-description"
                    dangerouslySetInnerHTML={{ __html: formatFeatureText(service.description) }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Pricing Card */}
          <div className="pricing-card-sticky">
            <div className="pricing-card-content">
              <h3 className="pricing-card-title">Pricing</h3>
              <div className={`pricing-display ${service.addons && service.addons.length > 0 ? 'has-addons' : ''}`}>
                {/* Left Column: Base Pricing */}
                <div className="pricing-left-column">
                  {/* Base Price Label */}
                  <div className="base-price-label">Base Price</div>
                  {/* Current Price Row - On Top */}
                  <div className="current-price-row">
                    <span className={`current-price ${!price ? 'price-request' : ''}`}>
                      {price ? formatCurrency(price, displayCurrency) : 'Price on request'}
                    </span>
                    {service.isSpecial && (
                      <span className="special-star">
                        <i className="fas fa-star"></i>
                        <span className="special-text">Special</span>
                      </span>
                    )}
                  </div>
                  
                  {/* Base Package Description - What's included in the core package */}
                  {(() => {
                    const baseIncluded = (service as any).basePriceIncluded;
                    const hasBaseIncluded = baseIncluded && typeof baseIncluded === 'string' && baseIncluded.trim().length > 0;
                    const hasFeatures = service.features && service.features.length > 0;
                    
                    if (!hasBaseIncluded && !hasFeatures) return null;
                    
                    return (
                      <div className="base-package-includes">
                        <div className="base-package-includes-label">
                          <i className="fas fa-check-circle"></i>
                          <span>Base Package Includes:</span>
                        </div>
                        {hasBaseIncluded ? (
                          <p className="base-package-description-card">{baseIncluded.trim()}</p>
                        ) : hasFeatures && service.features ? (
                          <ul className="base-package-features">
                            {service.features.slice(0, 3).map((feature, idx) => (
                              <li key={idx}>{feature.replace(/\*\*/g, '').replace(/\*/g, '')}</li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    );
                  })()}
                  
                  {/* Original Price Row - Only show if there's a discount */}
                  {hasDiscount && originalPrice && price && (
                    <div className="price-row">
                      <span className="original-price">
                        {formatCurrency(originalPrice, displayCurrency)}
                      </span>
                      <span className="discount-badge">
                        Save {formatCurrency(discountAmount, displayCurrency)}
                      </span>
                    </div>
                  )}

                  {/* Duration Badge - Below prices */}
                  {service.durationMinutes && (
                    <div className="duration-badge-bottom">
                      <span className="info-badge">
                        <i className="fas fa-clock"></i>
                        {formatDuration(service.durationMinutes)}
                      </span>
                    </div>
                  )}

                  {/* Category Badge - If exists */}
                  {((service.categories && service.categories.length > 0) || service.category) && (
                    <div className="category-badge-section">
                      {service.categories && service.categories.length > 0 ? (
                        <div className="info-badges-container">
                          {service.categories.map((categorySlug, idx) => {
                            const category = categories.find(c => c.slug === categorySlug);
                            return (
                              <span 
                                key={idx} 
                                className="info-badge"
                                style={{ backgroundColor: category?.color || '#8B4513' }}
                              >
                                <i className="fas fa-tag"></i>
                                {category?.name || categorySlug}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="info-badge">
                          <i className="fas fa-tag"></i>
                          {service.category}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Right Column: Add-ons Summary */}
                {service.addons && service.addons.length > 0 && (
                  <div className="pricing-right-column addons-summary-card">
                    <div className="addons-summary-header">
                      <span>Add-ons</span>
                      <span className="addons-count-pill">
                        {service.addons.length} option{service.addons.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="addons-summary-list">
                      {sortedAddons.map((addon, index) => {
                        const pricingLabel = formatPricingType(addon.pricingType);
                        const metaParts = [];
                        if (addon.description) metaParts.push(addon.description);
                        if (pricingLabel) metaParts.push(pricingLabel);
                        const metaText = metaParts.join(' · ');
                        return (
                          <div 
                            key={index} 
                            className={`addon-summary-row ${(!addon.price || addon.price === 0) ? 'included' : ''}`}
                          >
                            <div className="addon-summary-text">
                              <span className="addon-summary-name">{addon.name}</span>
                              {metaText && (
                                <span className="addon-summary-description">{metaText}</span>
                              )}
                            </div>
                            <span className="addon-summary-price">
                              {addon.price ? `+ ${formatAddonPrice(addon.price, displayCurrency, addon.showFromPrice || false)}` : 'Included'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Primary Content */}

          {/* What's Included - Value proposition */}
          {service.features && service.features.length > 0 && (
            <div className="content-section">
              <h3>What's Included</h3>
              <ul className="features-list">
                {service.features.map((feature, index) => (
                  <li key={index} dangerouslySetInnerHTML={{ __html: formatFeatureText(feature) }} />
                ))}
              </ul>
            </div>
          )}

          {/* Secondary Content - Further down */}
          
          {/* How It Works */}
          {customizationData?.showProcessSteps && getProcessSteps().length > 0 && (
            <div className="content-section">
              <h3>Process</h3>
              <div className="process-steps">
                {getProcessSteps().map((step) => (
                  <div key={step.id} className="process-step">
                    <div className="step-number">{step.number}</div>
                    <div className="step-content">
                      <h4>{step.title}</h4>
                      <p>{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* What to Expect */}
          {customizationData?.showExpectations && getExpectations().length > 0 && (
            <div className="content-section">
              <h3>What to Expect</h3>
              <div className="expectations-grid">
                {getExpectations().map((expectation) => (
                  <div key={expectation.id} className="expectation-item">
                    <i className={expectation.icon}></i>
                    <h4>{expectation.title}</h4>
                    <p>{expectation.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gallery Images */}
          {service.galleryImages && service.galleryImages.length > 0 && (
            <div className="content-section">
              <h3>Gallery</h3>
              <div className="gallery-grid">
                {service.galleryImages.map((image, index) => (
                  <div key={index} className="gallery-image-item">
                    <img 
                      src={getOptimizedImageUrl(
                        typeof image === 'string' 
                          ? image 
                          : (image.secure_url || image.url || image.publicId || ''),
                        'modal'
                      )}
                      alt={typeof image === 'string' ? `${service.name} - Image ${index + 1}` : (image.alt || `${service.name} - Image ${index + 1}`)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Testimonials */}
          {customizationData?.showTestimonials && getTestimonials().length > 0 && (
            <div className="content-section">
              <h3>What Clients Say</h3>
              <div className="testimonials-grid">
                {getTestimonials().map((testimonial) => (
                  <div key={testimonial.id} className="testimonial-item">
                    <p className="testimonial-content">"{testimonial.content}"</p>
                    <p className="testimonial-author">— {testimonial.author}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Related Content */}
          {(relatedGalleries.length > 0 || relatedBlogPosts.length > 0) && (
            <div className="content-section">
              {relatedGalleries.length > 0 && (
                <div className="related-galleries-section">
                  <h3>Related Galleries</h3>
                  <div className="related-galleries-grid">
                    {relatedGalleries.map((gallery) => (
                      <div 
                        key={gallery.id} 
                        className="related-gallery-card"
                        onClick={() => {
                          onClose();
                          navigate(sitePaths.galleryPath(gallery));
                        }}
                      >
                        {gallery.heroImage && (
                          <div className="gallery-card-image">
                            <img 
                              src={gallery.heroImage.secure_url || gallery.heroImage.url || '/placeholder.jpg'}
                              alt={gallery.name}
                            />
                          </div>
                        )}
                        <div className="gallery-card-content">
                          <h4>{gallery.name}</h4>
                          <p>{gallery.images.length} image{gallery.images.length !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {relatedBlogPosts.length > 0 && (
                <div className="related-blogs-section">
                  <h3>Related Blog Posts</h3>
                  <div className="related-blogs-grid">
                    {relatedBlogPosts.map((post) => (
                      <div 
                        key={post.id} 
                        className="related-blog-card"
                        onClick={() => {
                          onClose();
                          navigate(`/blog/${post.slug}`);
                        }}
                      >
                        {post.heroImage && (
                          <div className="blog-card-image">
                            <img 
                              src={typeof post.heroImage === 'string' 
                                ? post.heroImage 
                                : (post.heroImage.secure_url || post.heroImage.url || '/placeholder.jpg')}
                              alt={post.title}
                            />
                          </div>
                        )}
                        <div className="blog-card-content">
                          <h4>{post.title}</h4>
                          {post.excerpt && (
                            <p className="blog-excerpt">{post.excerpt.substring(0, 100)}...</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Booking Note */}
          <div className="booking-note">
            <i className="fas fa-info-circle"></i>
            <span>{getBookingNote()}</span>
          </div>

          {/* Contact Button */}
          <div className="service-whatsapp-contact">
            <button
              className="whatsapp-contact-button"
              onClick={() => setShowContactModal(true)}
            >
              <i className="fas fa-envelope"></i>
              <span>Contact me about this package</span>
            </button>
          </div>

          {/* Share Button */}
          <div className="service-share">
            <ShareButton
              content={shareContent}
              variant="both"
              size="medium"
              platforms={['whatsapp', 'twitter', 'facebook', 'instagram', 'email']}
              showCopyButton={true}
              onShare={() => {}}
            />
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && service && (
        <div className="service-contact-modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="service-contact-modal" onClick={(e) => e.stopPropagation()}>
            <button className="service-contact-modal-close" onClick={() => setShowContactModal(false)}>
              <i className="fas fa-times"></i>
            </button>
            <h3>Contact Me About This Package</h3>
            <p className="service-contact-modal-subtitle">
              I'd love to discuss the <strong>{service.name}</strong> package with you! Choose how you'd like to reach out.
            </p>
            
            <InquiryDatePicker />
            <div className="service-contact-options">
              <a 
                href={getWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="service-contact-option-btn whatsapp-btn"
                onClick={() => setShowContactModal(false)}
              >
                <i className="fab fa-whatsapp"></i>
                <div className="service-contact-option-content">
                  <span className="service-contact-option-title">WhatsApp</span>
                  <span className="service-contact-option-subtitle">Send a quick message</span>
                </div>
                <i className="fas fa-arrow-right"></i>
              </a>
              
              <a 
                href={getPhoneUrl()}
                className="service-contact-option-btn phone-btn"
                onClick={() => setShowContactModal(false)}
              >
                <i className="fas fa-phone"></i>
                <div className="service-contact-option-content">
                  <span className="service-contact-option-title">Phone</span>
                  <span className="service-contact-option-subtitle">Call me directly</span>
                </div>
                <i className="fas fa-arrow-right"></i>
              </a>
              
              <a 
                href={getEmailUrl()}
                className="service-contact-option-btn email-btn"
                onClick={() => setShowContactModal(false)}
              >
                <i className="fas fa-envelope"></i>
                <div className="service-contact-option-content">
                  <span className="service-contact-option-title">Email</span>
                  <span className="service-contact-option-subtitle">Send an email inquiry</span>
                </div>
                <i className="fas fa-arrow-right"></i>
              </a>
            </div>

            <div className="service-contact-message-preview">
              <p className="service-preview-label">Your message will include:</p>
              <div className="service-preview-content">
                <div className="service-preview-item">
                  <i className="fas fa-check"></i>
                  <span>Package: {service.name}</span>
                </div>
                {price && (
                  <div className="service-preview-item">
                    <i className="fas fa-check"></i>
                    <span>Price: {formatCurrency(price, displayCurrency)}</span>
                  </div>
                )}
                {service.durationMinutes && (
                  <div className="service-preview-item">
                    <i className="fas fa-check"></i>
                    <span>Duration: {formatDuration(service.durationMinutes)}</span>
                  </div>
                )}
                {service.category && (
                  <div className="service-preview-item">
                    <i className="fas fa-check"></i>
                    <span>Category: {service.category}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render modal in a portal to ensure it appears above all other elements
  return createPortal(modalContent, document.body);
};

export default PackageModal;
